import * as React from 'react'
import { useInfiniteQueryAdminUsers, UserListRequestPayload, UserRoleAssignment, UserListItem } from '@/lib/api/company/configuration/adminPanel/users'
import { useTable } from '@/modules/drivers/context/TableContext'
import UserRow from './UserRow'
import { companyRolesOf } from './RoleManagementPopover'
import { Loader } from '@/ui'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import TableHeaderCheckbox from './TableHeaderCheckbox'
import { useMutationUpdateUserRoles } from '@/lib/api/company/configuration/adminPanel/users/useMutationUpdateUserRoles'
import { EditUserModal, type UserFormValues } from '@/modules/settings/components/modal/ModalUsers'
import { useMutationAdminUserUpdate } from '@/lib/api/company/configuration/adminPanel/users'
import { useQueryCompanyRoles } from '@/lib/api/company/roles'
import { useQueryDrivers } from '@/lib/api/company/drivers'
import { useQueryDriverGroups } from '@/lib/api/company/drivers/useQueryDriverGroups'
import { useQueryDriverGroupAccess } from '@/lib/api/company/driverGroupAccess'
import { useQueryVehicleGroups } from '@/lib/api/company/vehicle-list/useQueryVehicleGroups'
import {
  ServiceAccountUsersNotice,
  useServiceAccountUsersContext,
} from '@/modules/shared/components/serviceAccount/ServiceAccountUserRoles'

interface UsersTableProps {
  companyId: string
}

const PER_PAGE = 20

const UsersTable: React.FC<UsersTableProps> = ({ companyId }) => {
  const { t } = useLocalize()
  const { selectedGroup, toggleAll, isSelectedAny, setAllIds } = useTable()

  const [filters, setFilters] = React.useState<UserListRequestPayload>({
    take: PER_PAGE,
    skip: 0,
    orderBy: 'createdDate',
    descending: true,
  })

  const [editing, setEditing] = React.useState<null | UserFormValues>(null)

  const q = useInfiniteQueryAdminUsers({ companyId, payload: filters })
  const { users, hasNextPage, isFetchingNextPage, fetchNextPage } = q
  const hasUsers = users && users.length > 0

  const isSelectedAll = selectedGroup?.ids.length === users.length && users.length > 0

  const serviceAccount = useServiceAccountUsersContext(companyId)
  const mutationUpdateRoles = useMutationUpdateUserRoles(companyId)
  const mutationUpdate = useMutationAdminUserUpdate(companyId)

  const { data: rolesData } = useQueryCompanyRoles(companyId)
  // Dostęp do grup kierowców: jedno zapytanie na tabelę, nie na wiersz.
  const showDriverAccess = !serviceAccount.isService
  const driverAccess = useQueryDriverGroupAccess(companyId, showDriverAccess)
  const driverGroups = useQueryDriverGroups({ companyId, enabled: showDriverAccess })
  const vehicleGroups = useQueryVehicleGroups({ companyId, enabled: showDriverAccess })
  // Bez uprawnień do listy dostępu lub grup kolumny pokazywałyby „wszyscy”/„usunięta grupa”.
  const showAccessColumns = showDriverAccess && !driverAccess.isError && !driverGroups.isError && !vehicleGroups.isError
  const rolesOptions = React.useMemo(() => {
    return rolesData?.roles.map(role => ({
      value: role.id.toString(),
      label: role.name,
      isDriver: role.isDriver,
    })) || []
  }, [rolesData])

  const driversQuery = useQueryDrivers({ companyId })
  const driversOptions = React.useMemo(() => {
    const data = driversQuery.data
    if (!data) return []
    const list = Array.isArray(data) ? data : (data as any).drivers ?? []
    return list.map((d: { id: number; name: string; surname: string }) => ({
      value: String(d.id),
      label: `${d.name} ${d.surname}`,
    }))
  }, [driversQuery.data])

  // Jak serwer (CanReadDriverData): rola kierowcy bez wglądu w aktywności widzi tylko siebie.
  const driverOnlyOf = (user: UserListItem) => {
    if (user.globalAdmin || user.paymentAdmin || user.companyAdmin || user.demoCompanyAdmin) return undefined
    const roles = companyRolesOf(user, companyId).flatMap((r) => rolesData?.roles.find((x) => x.id === r.id) ?? [])
    if (!roles.some((r) => r.isDriver) || roles.some((r) => r.canViewActivities || r.canEditActivities)) return undefined
    const driverId = user.driverEntityId ? String(user.driverEntityId) : null
    return {
      hasDriver: !!driverId,
      driverName: driversOptions.find((d: { value: string }) => d.value === driverId)?.label ?? null,
    }
  }

  const handleEditSubmit = async (values: UserFormValues) => {
    if (!editing?.userId) return
    await mutationUpdate.mutateAsync({
      userIdToUpdate: String(editing.userId),
      payload: {
        login: values.login,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.roleId ? { id: values.roleId } : undefined,
        driverId: values.driverId ? parseInt(values.driverId, 10) : undefined,
      },
      filters,
    })
    setEditing(null)
  }

  const handleRolesUpdate = React.useCallback(async (user: UserListItem, roles: UserRoleAssignment[]) => {
    await mutationUpdateRoles.mutateAsync({ user, roles, filters })
  }, [mutationUpdateRoles, filters])

  // Update allIds when users change
  React.useEffect(() => {
    if (users.length > 0) {
      setAllIds(users.map((user) => ({ id: user.id, isActive: true })))
    }
  }, [users, setAllIds])

  const sentinelRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) void fetchNextPage()
      },
      { threshold: 0.1 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, users.length])

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      orderBy: field,
      descending: prev.orderBy === field ? !prev.descending : true,
      skip: 0,
    }))
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (filters.orderBy !== field) {
      return <span className="tw-text-gray-400 tw-ml-1">↕</span>
    }
    return filters.descending ? (
      <span className="tw-text-blue-600 tw-ml-1">↓</span>
    ) : (
      <span className="tw-text-blue-600 tw-ml-1">↑</span>
    )
  }

  if (q.isLoading) {
    return (
      <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
        <Loader size="lg" />
      </div>
    )
  }

  if (!hasUsers) {
    return (
      <div className="tw-flex tw-h-64 tw-items-center tw-justify-center tw-text-gray-500">
        {t('usersModule.noUsers')}
      </div>
    )
  }

  return (
    <>
    <EditUserModal
      isOpen={Boolean(editing)}
      initialValues={editing}
      rolesOptions={rolesOptions}
      driversOptions={driversOptions}
      onCancel={() => setEditing(null)}
      onSubmit={handleEditSubmit}
    />
    {serviceAccount.isService && (
      <ServiceAccountUsersNotice
        href={serviceAccount.canManage ? `/et/company/${companyId}/serviceAccount` : undefined}
      />
    )}
    <div className="tw-overflow-x-auto">
      <table className="tw-w-full tw-border-collapse">
        <thead className="tw-bg-content-primary">
          <tr>
            <TableHeaderCheckbox
              checked={isSelectedAll}
              onChange={(checked) => toggleAll(checked)}
              isSelectedAny={isSelectedAny}
            />
            <th
              className="tw-cursor-pointer tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80 hover:tw-bg-base-200"
              onClick={() => handleSort('locked')}
            >
              Status
              <SortIcon field="locked" />
            </th>
            <th
              className="tw-cursor-pointer tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80 hover:tw-bg-base-200"
              onClick={() => handleSort('login')}
            >
              {t('usersModule.loginEmail')}
              <SortIcon field="login" />
            </th>
            <th className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
              {t('usersModule.role')}
            </th>
            {showAccessColumns && (
              <>
                <th className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
                  {t('usersModule.driverAccess.column')}
                </th>
                <th className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
                  {t('usersModule.vehicleAccess.column')}
                </th>
              </>
            )}
            <th className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
              {t('usersModule.addedBy')}
            </th>
            <th
              className="tw-cursor-pointer tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80 hover:tw-bg-base-200"
              onClick={() => handleSort('createdDate')}
            >
              {t('usersModule.createdDate')}
              <SortIcon field="createdDate" />
            </th>
            <th
              className="tw-cursor-pointer tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80 hover:tw-bg-base-200"
              onClick={() => handleSort('lastLogin')}
            >
              {t('usersModule.lastLogin')}
              <SortIcon field="lastLogin" />
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              companyId={companyId}
              onRolesUpdate={handleRolesUpdate}
              driverAccess={
                showAccessColumns
                  ? {
                      access: driverAccess.data,
                      driverGroups: driverGroups.data ?? [],
                      vehicleGroups: vehicleGroups.data ?? [],
                      driverOnly: driverOnlyOf(user),
                    }
                  : undefined
              }
              serviceAccount={
                serviceAccount.isService
                  ? { membership: serviceAccount.rosterById.get(user.id) }
                  : undefined
              }
              onDoubleClick={() => {
                const companyRole = user.roles?.find(ra => String(ra.companyId) === String(companyId))
                const firstRole = companyRole?.roles?.[0]
                setEditing({
                  login: user.login,
                  firstName: user.firstName,
                  lastName: user.surname,
                  roleId: firstRole ? String(firstRole.id) : '',
                  driverId: user.driverEntityId ? String(user.driverEntityId) : '',
                  userId: user.id,
                })
              }}
            />
          ))}
        </tbody>
      </table>
      {hasNextPage && (
        <div ref={sentinelRef} className="tw-flex tw-justify-center tw-py-4">
          {isFetchingNextPage && <Loader size="sm" />}
        </div>
      )}
    </div>
    </>
  )
}

export default UsersTable

